import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from './store';
import { useCallback, useState } from 'react';

/**
 * Hook that stops an onClick action. Useful when making a link or button seem disabled, but the
 * element is still able to be tabbed and read by screen readers. Nothing will happen when they
 * click on the element if intercept is true.  If intercept is false, any optional onClick props
 * will be executed.
 */
export function useHandleOnClickWithIntercept<Type = HTMLElement>(
  intercept?: boolean,
  onX?: (ev: React.MouseEvent<Type>) => void
) {
  const handleOnClick = useCallback(
    (ev: React.MouseEvent<Type>) => {
      if (intercept) {
        ev.preventDefault();
        ev.stopPropagation();
      } else if (onX) {
        onX(ev);
      }
    },
    [intercept, onX]
  );

  return handleOnClick;
}

export function useHandleOnMouseDownWithIntercept<Type = HTMLElement>(
  intercept?: boolean,
  onX?: (ev: React.MouseEvent<Type>) => void
) {
  const handleOnMouseDown = useCallback(
    (ev: React.MouseEvent<Type>) => {
      if (intercept) {
        ev.preventDefault();
        ev.stopPropagation();
      } else if (onX) {
        onX(ev);
      }
    },
    [intercept, onX]
  );

  return handleOnMouseDown;
}

/**
 * Hook that stops a onKeyDown action. Useful when making a link or button seem disabled, but the
 * button is still able to be tabbed and read by screen readers. Nothing will happen when they press
 * a key on the button if intercept is true.  If intercept is false, any optional onClick props wil
 * be executed.
 */
export function useHandleOnKeyDownWithIntercept<Type = HTMLElement>(
  intercept?: boolean,
  onX?: (ev: React.KeyboardEvent<Type>) => void
) {
  const handleOnKeyDown = useCallback(
    (ev: React.KeyboardEvent<Type>) => {
      if (ev.code === 'Tab') {
        // People should be able to tab through a disabled input or button.
        // Without this exception, people using keyboards to navigate get stuck.
      } else if (intercept) {
        ev.preventDefault();
        ev.stopPropagation();
      } else if (onX) {
        onX(ev);
      }
    },
    [intercept, onX]
  );

  return handleOnKeyDown;
}

/**
 * Hook that stops a onChange action. Useful when making a radio button or checkbox form element
 * seem disabled, but the user is still able to be tabbed and read by screen readers. Nothing will
 * happen when they try to select the checkbox or radio button.  If intercept is false, any optional
 * onChange props will be executed.
 */
export const useHandleOnChangeWithIntercept = (
  intercept?: boolean,
  onX?: React.ChangeEventHandler<HTMLElement>
) => {
  const handleOnChange = useCallback(
    (ev: React.ChangeEvent<HTMLElement>) => {
      if (intercept) {
        ev.preventDefault();
        ev.stopPropagation();
      } else if (onX) {
        onX(ev);
      }
    },
    [intercept, onX]
  );

  return handleOnChange;
};

// Use throughout your app instead of plain `useDispatch` and `useSelector`
type DispatchFunc = () => AppDispatch;
/**
 * A hook to access the redux dispatch function.  This hook has been pre-typed for convenience. Use
 * throughout your app instead of plain `useDispatch`
 */
export const useAppDispatch: DispatchFunc = useDispatch;

/**
 * A hook to access the redux store's state. This hook has been pre-typed for convenience. Use
 * throughout your app instead of plain `useDispatch` This hook takes a selector function as an
 * argument. The selector is called with the store state.
 *
 * This hook takes an optional equality comparison function as the second parameter that allows you
 * to customize the way the selected state is compared to determine whether the component needs to
 * be re-rendered.
 *
 * @param selector — the selector function
 * @param equalityFn — the function that will be used to determine equality
 * @returns — the selected state
 * @example ```typescript
 * import React from 'react'
 * import { useSelector } from 'react-redux'
 *
 * export const CounterComponent = () => {
 *   const counter = useSelector(state => state.counter)
 *   return <div>{counter}</div>
 * }
 * ```
 **/
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * A hook to track if an input has experienced an change event.  If it has not, we consider that
 * input to still be "pristine" or untouched.  Otherwise the input is "tainted" or touched.  Pass in
 * an optional onChange event to call after pristine state is altered.
 *
 * Returns an array with isPristine set to true or false and a change event handler function
 */
export function useTrackPristine<Type = HTMLElement>(
  onChange?: React.ChangeEventHandler<Type>
): [boolean, React.ChangeEventHandler<Type>] {
  const [isPristine, setIsPristine] = useState<boolean>(true);
  const handleOnChange = useCallback(
    (ev: React.ChangeEvent<Type>) => {
      setIsPristine(false);
      if (typeof onChange === 'function') {
        onChange(ev);
      }
    },
    [onChange]
  );

  return [isPristine, handleOnChange];
}
